import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { displaySpecLabel, localizedMarketText, localizedRecordLabel } from '@/i18n/catalogDisplayText';
import { useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import HoverCopyField from '@/tiktok-platform/modules/products/components/HoverCopyField';
import TiktokProductThumb from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import { downloadCsv } from '@/tiktok-platform/modules/products/components/TiktokPageToolbar';
import TiktokOpsListToolbar from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import BatchQueryModal from '@/tiktok-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/tiktok-platform/modules/products/components/batchQueryUtils';
import { openTiktokPathInNewTab } from '@/tiktok-platform/constants/tiktokRoutes';
import { useTiktokPageShopScope, stackShopDimensionTabs } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { useTiktokShop } from '@/tiktok-platform/context/TiktokShopContext';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import { checkTiktokProductEditable } from '@/tiktok-platform/utils/checkTiktokProductEditable';
import { fetchAllPages } from '@/tiktok-platform/utils/tiktokExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import {
  buildSiteNameByAbbrMap,
  formatSiteListSyncedAt,
  resolveSubSiteDisplayName,
} from '@/tiktok-platform/utils/tiktokSiteList';
import { TIKTOK_MODE_FULL } from '@/tiktok-platform/utils/tiktokShopCapabilities';
import {
  PUBLISH_RECORD_STATUS_TABS,
  resolvePublishStatusTone,
} from '@/tiktok-platform/utils/tiktokPublishRecordStatus';
import { normalizeTiktokShopType } from '@/utils/platformShopCatalog';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

const PUBLISH_RECORD_EXPORT_HEADERS = [
  'id', 'title', 'supplierCode', 'platformSpuName', 'status', 'site', 'action', 'source',
  'createdAt', 'publishedAt', 'finishedAt', 'listedAt', 'hopeOnSaleDate',
];

const PUBLISH_RECORDS_SYNCED_AT_KEY = 'ipe.tiktok.publishRecords.lastSyncedAt';

function publishRecordsSyncedAtStorageKey(shopId) {
  return `${PUBLISH_RECORDS_SYNCED_AT_KEY}.${shopId || ''}`;
}

function readStoredPublishRecordsSyncedAt(shopId) {
  if (!shopId || typeof localStorage === 'undefined') return '';
  try {
    return String(localStorage.getItem(publishRecordsSyncedAtStorageKey(shopId)) || '').trim();
  } catch {
    return '';
  }
}

function writeStoredPublishRecordsSyncedAt(shopId, iso) {
  if (!shopId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(publishRecordsSyncedAtStorageKey(shopId), String(iso || ''));
  } catch {
    /* ignore */
  }
}

const ID_TYPE_OPTIONS = [
  { value: 'skc', labelKey: 'publishRecord.productId' },
  { value: 'sku', label: 'SKU' },
];

const EMPTY_FILTERS = {
  idType: 'skc',
  idValue: '',
  title: '',
  supplierCode: '',
};

const EMPTY_QUERY = {
  status: '',
  idType: 'skc',
  keywords: '',
  title: '',
  supplierCode: '',
};

const BATCH_FIELDS = {
  idValue: {
    titleKey: 'idType',
    titleMap: { skc: 'tiktokCatalog.batchQueryProductId', sku: 'publishRecord.batchSKU' },
    placeholder: 'publishRecord.batchCodePh',
  },
  title: {
    title: 'publishRecord.batchTitle',
    placeholder: 'publishRecord.batchTitlePh',
  },
  supplierCode: {
    title: 'publishRecord.batchSupplier',
    placeholder: 'publishRecord.batchSupplierPh',
  },
};

const STATUS_TABS = PUBLISH_RECORD_STATUS_TABS;

function statusTabCount(stats, tab) {
  if (!stats) return 0;
  if (tab.id === 'all') return Number(stats.all) || 0;
  return Number(stats[tab.status]) || 0;
}

function resolveStatusLabel(item) {
  return String(item?.statusLabel ?? item?.status ?? '待审核').trim() || '待审核';
}

function resolveSkcStatusLabel(skc, record) {
  // 与 Tab 计数一致：优先整单 statusLabel（已归一 platformProductStatus）
  const recordLabel = resolveStatusLabel(record);
  if (recordLabel) return recordLabel;
  return String(skc?.statusLabel ?? skc?.status ?? '待审核').trim() || '待审核';
}

/** 当前店铺经营站点展示：全托管固定 ALL */
function formatCurrentShopSiteLabel(shop, siteNames = []) {
  const type = normalizeTiktokShopType(shop?.shopType);
  if (type === TIKTOK_MODE_FULL) return 'ALL';
  const names = (Array.isArray(siteNames) ? siteNames : [])
    .map((n) => String(n || '').trim())
    .filter(Boolean);
  if (names.length === 1 && names[0].toUpperCase() === 'ALL') return 'ALL';
  if (names.length) return names.join('、');
  const region = String(shop?.tiktokCountry || shop?.region || shop?.country || '').trim();
  if (!region) return '—';
  if (region.toUpperCase() === 'ALL') return 'ALL';
  return resolveSubSiteDisplayName(region) || region;
}

function resolveRecordSiteLabel(item, shopSiteLabel) {
  const fromItem = String(item?.siteLabel || '').trim();
  if (fromItem) return fromItem;
  const listed = (Array.isArray(item?.listedSites) ? item.listedSites : [])
    .map((n) => String(n || '').trim())
    .filter(Boolean);
  if (listed.length) return listed.join('、');
  return shopSiteLabel || '—';
}

function canDeleteRecord(item) {
  if (item?.canDelete === false) return false;
  return Boolean(item?.id);
}

function formatUpdatedAt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function RecordSkcThumb({ imageUrl }) {
  return <TiktokProductThumb imageUrl={imageUrl} />;
}

/** 发布记录：布局对齐草稿箱，并增加状态列；同步更新走 Get Product audit.status */
export default function ProductPublishRecordsPage() {
  const { t, i18n } = useTranslation('workbench');
  const { shopDimensionTabs, concreteScopeId, allShopsScope } = useTiktokPageShopScope();
  const { shops = [] } = useTiktokShop();
  const shopId = concreteScopeId;
  const activeShop = useMemo(
    () => (shops || []).find((s) => String(s.id) === String(shopId)) || null,
    [shops, shopId],
  );
  const location = useLocation();
  const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();

  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [statusDimension, setStatusDimension] = useState('all');
  const [statusCounts, setStatusCounts] = useState(null);
  const [queryNonce, setQueryNonce] = useState(0);
  const [batchModalField, setBatchModalField] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [toolbarBusy, setToolbarBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(() => readStoredPublishRecordsSyncedAt(''));
  const [shopSiteNames, setShopSiteNames] = useState([]);
  const selectedRowsRef = useRef(new Map());

  const shopSiteLabel = useMemo(
    () => formatCurrentShopSiteLabel(activeShop, shopSiteNames),
    [activeShop, shopSiteNames],
  );

  useEffect(() => {
    let cancelled = false;
    async function loadShopSites() {
      if (!shopId) {
        setShopSiteNames([]);
        return;
      }
      const type = normalizeTiktokShopType(activeShop?.shopType);
      if (type === TIKTOK_MODE_FULL) {
        setShopSiteNames(['ALL']);
        return;
      }
      try {
        const res = await tiktokPlatformAPI.getShopSiteList(Number(shopId));
        if (cancelled) return;
        const map = buildSiteNameByAbbrMap(res?.siteList ?? res);
        const names = Array.from(new Set(
          Array.from(map.values()).map((n) => String(n || '').trim()).filter(Boolean),
        ));
        setShopSiteNames(names);
      } catch {
        if (!cancelled) setShopSiteNames([]);
      }
    }
    loadShopSites();
    return () => { cancelled = true; };
  }, [shopId, activeShop?.shopType]);

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedIds.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      if (row?.id != null && selectedIds.has(row.id)) map.set(row.id, row);
    });
  }, [items, selectedIds]);

  // 发品成功跳转进来：强制重新拉列表
  useEffect(() => {
    if (!location.state?.refreshPublishRecords) return undefined;
    setPage(1);
    setQueryNonce((n) => n + 1);
    const t = window.setTimeout(() => setQueryNonce((n) => n + 1), 800);
    return () => window.clearTimeout(t);
  }, [location.state?.refreshPublishRecords, location.state?.at]);

  const loadRecords = useCallback(async () => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setStatusCounts(null);
      setSelectedIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const res = await tiktokPlatformAPI.listPublishRecords(shopId, {
        status: query.status || undefined,
        idType: query.keywords ? (query.idType || 'skc') : undefined,
        keywords: query.keywords || undefined,
        title: query.title || undefined,
        supplierCode: query.supplierCode || undefined,
        page,
        pageSize,
      });
      const nextItems = Array.isArray(res?.items) ? res.items : [];
      setItems(nextItems);
      setTotal(Number(res?.total) || 0);
      const counts = res?.statusCounts && typeof res.statusCounts === 'object' ? res.statusCounts : null;
      setStatusCounts(counts);
    } catch (e) {
      setItems([]);
      setTotal(0);
      setStatusCounts(null);
      showToast(e?.message || t('publishRecord.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, query.status, query.idType, query.keywords, query.title, query.supplierCode, page, pageSize, t]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords, queryNonce]);

  useEffect(() => {
    setFilterDraft(EMPTY_FILTERS);
    setQuery(EMPTY_QUERY);
    setBatchModalField(null);
    setPage(1);
    setQueryNonce((n) => n + 1);
    setSelectedIds(new Set());
    setLastSyncedAt(readStoredPublishRecordsSyncedAt(shopId));
  }, [shopId]);

  const refreshAuditStatusesAction = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickShop'), 'warning');
      return;
    }
    const track = await startJob({
      kind: 'publishRecord',
      shopId,
      label: t('publishRecord.syncUpdate'),
      silent: true,
    });
    if (!track?.id) return;
    await new Promise((resolve) => {
      const unsub = onTrackDone(track.id, async (result) => {
        unsub();
        if (result?.ok) {
          const latest = result.view || {};
          const refreshed = Number(latest.imported) || 0;
          const finishedAt = latest.finishedAt || new Date().toISOString();
          setLastSyncedAt(String(finishedAt));
          writeStoredPublishRecordsSyncedAt(shopId, finishedAt);
          showToast(
            refreshed > 0
              ? t('publishRecord.synced', { count: refreshed })
              : (latest.message || t('publishRecord.nothingToSync')),
            refreshed > 0 ? 'success' : 'info',
          );
          await loadRecords();
        }
        resolve();
      });
    });
  }, [shopId, loadRecords, startJob, onTrackDone, t]);

  const {
    refresh: onSyncStatuses,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(refreshAuditStatusesAction);

  const syncingStatuses = syncingLocal || isBusy('publishRecord', shopId);

  const latestSyncedLabel = lastSyncedAt
    ? t('publishRecord.lastUpdate', { time: formatSiteListSyncedAt(lastSyncedAt) })
    : t('publishRecord.notSynced');

  const selectableIds = useMemo(() => items.map((row) => row.id).filter(Boolean), [items]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    setSelectedIds((prev) => togglePageSelection(prev, selectableIds, allSelected));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const openBatchModal = (fieldKey) => {
    setBatchModalField(fieldKey);
  };

  const onBatchConfirm = (tokens) => {
    if (!batchModalField) return;
    updateField(batchModalField, joinBatchTokens(tokens));
  };

  const batchModalMeta = batchModalField ? BATCH_FIELDS[batchModalField] : null;
  const batchModalTitle = t(batchModalMeta
    ? (batchModalMeta.title
      || batchModalMeta.titleMap?.[filterDraft[batchModalMeta.titleKey]]
      || 'publishRecord.batchQuery')
    : 'publishRecord.batchQuery');
  const idTypeOption = ID_TYPE_OPTIONS.find((o) => o.value === filterDraft.idType);
  const idTypeLabel = idTypeOption?.labelKey
    ? t(idTypeOption.labelKey)
    : (idTypeOption?.label || t('publishRecord.productId'));

  const onQuery = () => {
    let idTokens = parseBatchTokens(filterDraft.idValue).slice(0, BATCH_QUERY_LIMIT);
    let titleTokens = parseBatchTokens(filterDraft.title).slice(0, 5);
    let supplierTokens = parseBatchTokens(filterDraft.supplierCode).slice(0, 5);
    const idType = filterDraft.idType || 'skc';
    if (idType !== 'spu' && idTokens.length > 20) {
      idTokens = idTokens.slice(0, 20);
      showToast(t('publishRecord.batchIdCap'), 'info');
    } else if (parseBatchTokens(filterDraft.idValue).length > BATCH_QUERY_LIMIT) {
      showToast(t('publishRecord.batchCodeCap', { limit: BATCH_QUERY_LIMIT }), 'info');
    }
    if (parseBatchTokens(filterDraft.title).length > 5) {
      showToast(t('publishRecord.batchTitleCap'), 'info');
    }
    if (parseBatchTokens(filterDraft.supplierCode).length > 5) {
      showToast(t('publishRecord.batchSupplierCap'), 'info');
    }
    if (shopId) setLoading(true);
    setPage(1);
    setQuery({
      status: statusDimension === 'all' ? '' : statusDimension,
      idType,
      keywords: idTokens.join(','),
      title: titleTokens.join(','),
      supplierCode: supplierTokens.join(','),
    });
    setQueryNonce((n) => n + 1);
  };

  const onStatusDimensionChange = (nextId) => {
    if (!nextId || nextId === statusDimension) return;
    setStatusDimension(nextId);
    setPage(1);
    setSelectedIds(new Set());
    if (shopId) setLoading(true);
    setQuery((prev) => ({
      ...prev,
      status: nextId === 'all' ? '' : nextId,
    }));
    setQueryNonce((n) => n + 1);
  };

  const statusTabsInner = useMemo(() => (
    <div className="tko-status-tabs" role="tablist" aria-label={t('publishRecord.tabsAria')}>
      {STATUS_TABS.map((tab) => {
        const active = statusDimension === tab.id;
        const count = statusTabCount(statusCounts, tab);
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tko-status-tab${active ? ' tko-status-tab--active' : ''}`}
            onClick={() => onStatusDimensionChange(tab.id)}
          >
            {t(`publishRecord.${({
              all: 'tabAll',
              '待审核': 'tabPendingReview',
              '已通过': 'tabApproved',
              '已上架': 'tabListed',
              '已驳回': 'tabRejected',
              '待商家确认': 'tabMerchant',
              '处理中': 'tabProcessing',
              '接受失败': 'tabAcceptFailed',
              '已撤回': 'tabWithdrawn',
              '申诉中': 'tabAppeal',
            })[tab.id] || 'tabAll'}`, { defaultValue: tab.label })}
            <span className="tko-status-tab__count">{count}</span>
          </button>
        );
      })}
    </div>
  ), [statusDimension, statusCounts, t]);

  const statusTabs = useMemo(
    () => stackShopDimensionTabs(shopDimensionTabs, statusTabsInner),
    [shopDimensionTabs, statusTabsInner],
  );

  const onReset = () => {
    setFilterDraft(EMPTY_FILTERS);
    setStatusDimension('all');
    setQuery(EMPTY_QUERY);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const onCopyToDraft = async (item) => {
    if (!shopId) {
      showToast(t('publishRecord.pickShop'), 'warning');
      return;
    }
    if (!item?.id) {
      showToast(t('publishRecord.missingRecord'), 'warning');
      return;
    }
    if (copyingId) return;
    setCopyingId(item.id);
    try {
      const res = await tiktokPlatformAPI.copyPublishRecordToDraft(shopId, item.id, {
        spuName: item.platformSpuName || '',
      });
      const draftId = res?.id;
      showToast(t('publishRecord.copied'), 'success');
      if (draftId) {
        openTiktokPathInNewTab(`products/publish?draftId=${encodeURIComponent(draftId)}`);
      } else {
        openTiktokPathInNewTab('products/drafts');
      }
    } catch (e) {
      showToast(e?.message || t('publishRecord.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onEdit = async (item) => {
    // 有平台 SPU：先校验是否可编辑，再打开编辑页
    if (item?.platformSpuName) {
      if (!shopId) {
        showToast(t('publishRecord.pickShop'), 'warning');
        return;
      }
      setEditingId(item.id);
      try {
        const check = await checkTiktokProductEditable(shopId, item.platformSpuName);
        if (!check.ok) {
          showToast(check.reason || t('publishRecord.notEditable'), 'warning');
          return;
        }
        const qs = new URLSearchParams();
        if (item.id != null) qs.set('recordId', String(item.id));
        if (item.draftId != null) qs.set('draftId', String(item.draftId));
        const q = qs.toString();
        openTiktokPathInNewTab(
          `products/edit/${encodeURIComponent(item.platformSpuName)}${q ? `?${q}` : ''}`,
        );
      } finally {
        setEditingId(null);
      }
      return;
    }
    // 无 SPU（常见于历史「假成功」记录）：回填关联草稿表单；发品后草稿会软删，后端仍允许按 ID 读取
    if (item?.draftId) {
      openTiktokPathInNewTab(`products/publish?draftId=${encodeURIComponent(item.draftId)}`);
      return;
    }
    showToast(t('publishRecord.cannotEdit'), 'warning');
  };

  const writePublishRecordsCsv = useCallback((rows, filename) => {
    downloadCsv(
      filename,
      PUBLISH_RECORD_EXPORT_HEADERS,
      rows.map((row) => ({
        id: row.id,
        title: row.title,
        supplierCode: row.supplierCode,
        platformSpuName: row.platformSpuName,
        status: resolveStatusLabel(row),
        site: resolveRecordSiteLabel(row, shopSiteLabel),
        action: row.action,
        source: row.source,
        createdAt: row.createdAt,
        publishedAt: row.publishedAt || row.finishedAt,
        finishedAt: row.finishedAt,
        listedAt: row.listedAt || '',
        hopeOnSaleDate: row.hopeOnSaleDate || row.shelfLabel || '',
      })),
    );
  }, [shopSiteLabel]);

  const fetchAllPublishRecords = useCallback(async () => {
    if (!shopId) return [];
    return fetchAllPages(async (pageNo, size) => {
      const res = await tiktokPlatformAPI.listPublishRecords(shopId, {
        status: query.status || undefined,
        idType: query.keywords ? (query.idType || 'skc') : undefined,
        keywords: query.keywords || undefined,
        title: query.title || undefined,
        supplierCode: query.supplierCode || undefined,
        page: pageNo,
        pageSize: size,
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [shopId, query.status, query.idType, query.keywords, query.title, query.supplierCode]);

  const exportFilteredRecords = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const rows = await fetchAllPublishRecords();
      if (!rows.length) {
        showToast(t('publishRecord.noneToExport'), 'warning');
        return;
      }
      writePublishRecordsCsv(rows, `tiktok-publish-records-filtered-${Date.now()}.csv`);
      showToast(t('publishRecord.exported', { count: rows.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('publishRecord.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  }, [shopId, exporting, fetchAllPublishRecords, writePublishRecordsCsv, t]);

  const exportPageRecords = useCallback(() => {
    if (!items.length) {
      showToast(t('publishRecord.noneToExport'), 'warning');
      return;
    }
    writePublishRecordsCsv(items, `tiktok-publish-records-page-${Date.now()}.csv`);
    showToast(t('publishRecord.exported', { count: items.length }), 'success');
  }, [items, writePublishRecordsCsv, t]);

  const exportSelectedRecords = useCallback(() => {
    const rows = Array.from(selectedIds)
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    if (!rows.length) {
      showToast(t('publishRecord.pickToExport'), 'warning');
      return;
    }
    writePublishRecordsCsv(rows, `tiktok-publish-records-selected-${Date.now()}.csv`);
    showToast(t('publishRecord.exportedSelected', { count: rows.length }), 'success');
  }, [selectedIds, writePublishRecordsCsv, t]);

  const onBatchDelete = async () => {
    if (!shopId || selectedIds.size === 0) {
      showToast(t('publishRecord.pickToDelete'), 'warning');
      return;
    }
    const ids = Array.from(selectedIds)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('publishRecord.invalidSelection'), 'warning');
      return;
    }
    const confirmed = await showTiktokConfirm({
      title: t('publishRecord.batchDelete'),
      message: t('publishRecord.batchDeleteMsg', { count: ids.length, platform: 'TikTok' }),
      tone: 'danger',
      confirmText: t('publishRecord.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!confirmed) return;
    setToolbarBusy(true);
    try {
      const res = await tiktokPlatformAPI.batchDeletePublishRecords(shopId, ids);
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('publishRecord.deletedCount', { count }), 'success');
      } else {
        showToast(t('publishRecord.noneDeleted'), 'warning');
      }
      setSelectedIds(new Set());
      loadRecords();
    } catch (e) {
      showToast(e?.message || t('publishRecord.batchDeleteFailed'), 'error');
    } finally {
      setToolbarBusy(false);
    }
  };

  const colSpan = 8;

  return (
    <>
      <TiktokSyncProgressSlot kinds={['publishRecord']} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedIds.size}
            busy={toolbarBusy || loading || exporting}
            syncKinds={['publishRecord']}
            onSync={() => {
              if (!shopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'TikTok' }), 'warning');
                return;
              }
              onSyncStatuses();
            }}
            syncing={syncingStatuses}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncingStatuses}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('publishRecord.syncHintTiktok')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedIds.size,
              onExportFiltered: exportFilteredRecords,
              onExportPage: exportPageRecords,
              onExportSelected: exportSelectedRecords,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline tko-field--id-combo tko-field--batch">
              <select
                className="tko-select tko-select--compact"
                value={filterDraft.idType}
                disabled={!shopId}
                onChange={(e) => updateField('idType', e.target.value)}
                aria-label={t('publishRecord.idTypeAria')}
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.labelKey ? t(opt.labelKey) : opt.label}</option>
                ))}
              </select>
              <input
                className="tko-input"
                value={filterDraft.idValue}
                placeholder={t('publishRecord.idPlaceholder', { type: idTypeLabel })}
                disabled={!shopId}
                onChange={(e) => updateField('idValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('publishRecord.batchInputId', { type: idTypeLabel })}
                title={t('publishRecord.batchQuery')}
                disabled={!shopId}
                onClick={() => openBatchModal('idValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="tko-field tko-field--inline tko-field--batch">
              <span className="tko-field__label">{t('publishRecord.title')}</span>
              <input
                className="tko-input"
                value={filterDraft.title}
                placeholder={t('publishRecord.titlePlaceholder')}
                disabled={!shopId}
                onChange={(e) => updateField('title', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('publishRecord.batchInputTitle')}
                title={t('publishRecord.batchQuery')}
                disabled={!shopId}
                onClick={() => openBatchModal('title')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="tko-field tko-field--inline tko-field--batch">
              <span className="tko-field__label">{t('publishRecord.supplier')}</span>
              <input
                className="tko-input"
                value={filterDraft.supplierCode}
                placeholder={t('publishRecord.supplierPlaceholder')}
                disabled={!shopId}
                onChange={(e) => updateField('supplierCode', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('publishRecord.batchInputSupplier')}
                title={t('publishRecord.batchQuery')}
                disabled={!shopId}
                onClick={() => openBatchModal('supplierCode')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="tko-filter-bar__actions">
            <button type="button" className="tko-btn tko-btn--secondary tko-btn--sm" onClick={onReset} disabled={!shopId}>
              {t('publishRecord.reset')}
            </button>
            <button type="button" className="tko-btn tko-btn--primary tko-btn--sm" onClick={onQuery} disabled={!shopId}>
              {t('publishRecord.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={statusTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('publishRecord.querying')}
      >
        <div className={`wf-tasks-page platform-product-page wf-tasks-page--embedded platform-product-page--ops-chrome${loading ? ' platform-product-page--loading' : ''}`}>
          <div className="wf-tasks-table-panel">
            <div className="wf-tasks-table-wrap" role="region" aria-live="polite">
              <table className="wf-tasks-table platform-product-table platform-product-table--tiktok platform-product-table--drafts platform-product-table--records">
                <colgroup>
                  <col className="tiktok-product-col--check" />
                  <col className="tiktok-product-col--info" />
                  <col className="tiktok-product-col--skc" />
                  <col className="tiktok-product-col--type" />
                  <col className="tiktok-product-col--status" />
                  <col className="tiktok-product-col--remark" />
                  <col className="tiktok-product-col--time" />
                  <col className="tiktok-product-col--ops" />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="tiktok-product-cell--spu-check"
                      onClick={() => {
                        if (!shopId || loading || selectableIds.length === 0) return;
                        toggleAll();
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('publishRecord.selectAll')}
                        disabled={!shopId || loading || selectableIds.length === 0}
                        checked={allSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={toggleAll}
                      />
                    </th>
                    <th scope="col">{t('publishRecord.productInfo')}</th>
                    <th scope="col">{t('publishRecord.spec')}</th>
                    <th scope="col">{t('publishRecord.type')}</th>
                    <th scope="col">{t('publishRecord.status')}</th>
                    <th scope="col">{t('publishRecord.site')}</th>
                    <th scope="col">{t('publishRecord.time')}</th>
                    <th scope="col">{t('publishRecord.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!shopId || loading || items.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan} className="wf-tasks-table__empty">
                        <div className="funding-empty-state">
                          <p className="funding-empty-state__title">
                            {!shopId ? t('publishRecord.pickShop') : loading ? t('publishRecord.loading') : t('publishRecord.empty')}
                          </p>
                          {!shopId || loading ? null : (
                            <p className="funding-empty-state__description">
                              {t('publishRecord.emptyHint')}
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.flatMap((item) => {
                      const skcItems = Array.isArray(item.skcItems) && item.skcItems.length
                        ? item.skcItems
                        : [{ colorLabel: null, skcName: null, imageUrl: null, merchantSkus: [], supplierCode: null }];
                      const typeLabel = item.typeLabel
                        || (item.source === 'sync' ? t('publishRecord.sourceSync') : (item.action === 'edit' ? t('publishRecord.sourceEdit') : t('publishRecord.sourceNew')));
                      const rowSpan = Math.max(1, skcItems.length);
                      const createdAt = item.createdAt;
                      const publishedAt = item.publishedAt || item.finishedAt;
                      const shelfLabel = item.shelfLabel
                        || item.hopeOnSaleDate
                        || (item.shelfWay === '2' ? '—' : t('publishRecord.autoList'));

                      return skcItems.map((skc, skcIdx) => {
                        const isFirst = skcIdx === 0;
                        const siteLabel = resolveRecordSiteLabel(item, shopSiteLabel);
                        const supplierCode = String(
                          item.supplierCode
                          || skc.supplierCode
                          || '',
                        ).trim();

                        return (
                          <tr
                            key={`${item.id}-skc-${skcIdx}-${skc.skcName || 'empty'}`}
                            className={`tiktok-product-row tiktok-draft-row${isFirst ? '' : ' tiktok-draft-row--skc-child'}`}
                          >
                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell tiktok-product-cell--spu-check"
                                  onClick={() => toggleOne(item.id)}
                                >
                                  <input
                                    type="checkbox"
                                    aria-label={t('publishRecord.selectRecord', { title: item.title || item.id })}
                                    checked={selectedIds.has(item.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleOne(item.id)}
                                  />
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell tiktok-product-cell--info"
                                >
                                  <div className="tiktok-product-info tiktok-product-info--text-only tiktok-draft-product-info">
                                    <div className="tiktok-product-info__text">
                                      <HoverCopyField
                                        value={item.title || t('publishRecord.unnamed')}
                                        className="tiktok-product-info__title"
                                        popoverCopy
                                      />
                                      <HoverCopyField
                                        label={t('publishRecord.productId')}
                                        value={item.platformSpuName || item.productId || item.platformProductId || ''}
                                        className="tiktok-product-info__meta"
                                        separator=": "
                                      />
                                      <HoverCopyField
                                        label={t('publishRecord.supplier')}
                                        value={supplierCode}
                                        className="tiktok-product-info__meta"
                                        separator=": "
                                        emptyText="—"
                                      />
                                      <div className="tiktok-product-info__meta">
                                        {t('publishRecord.category', { label: item.categoryLabel || '—' })}
                                      </div>
                                      {item.errorMessage ? (
                                        <div className="tiktok-product-info__meta tiktok-record-error" title={item.errorMessage}>
                                          {t('publishRecord.note', { message: item.errorMessage })}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                              </>
                            ) : null}

                            <td className="tiktok-product-cell tiktok-product-cell--spec">
                              <div className="tiktok-product-spec tiktok-draft-skc">
                                <RecordSkcThumb imageUrl={skc.imageUrl} />
                                <div className="tiktok-product-spec__text">
                                  <div className="tiktok-product-spec__color">
                                    {displaySpecLabel(skc.colorLabel || skc.skcName) || '—'}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {isFirst ? (
                              <td
                                rowSpan={rowSpan}
                                className="tiktok-product-cell tiktok-draft-cell--type"
                              >
                                {localizedRecordLabel(t, typeLabel, i18n.language)}
                              </td>
                            ) : null}
                            <td className="tiktok-product-cell tiktok-draft-cell--status">
                              <span className={`tko-records__status tko-records__status--${resolvePublishStatusTone(resolveSkcStatusLabel(skc, item))}`}>
                                {localizedRecordLabel(t, resolveSkcStatusLabel(skc, item), i18n.language)}
                              </span>
                            </td>
                            {isFirst ? (
                              <td
                                rowSpan={rowSpan}
                                className="tiktok-product-cell tiktok-draft-cell--site"
                              >
                                {localizedMarketText(siteLabel, i18n.language)}
                              </td>
                            ) : null}
                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell platform-product-table__muted tiktok-draft-cell--time"
                                >
                                  <div className="tiktok-record-times">
                                    <div className="tiktok-record-times__row">
                                      <span className="tiktok-record-times__label">{t('publishRecord.createdLabel')}</span>
                                      <span>{formatUpdatedAt(createdAt)}</span>
                                    </div>
                                    <div className="tiktok-record-times__row">
                                      <span className="tiktok-record-times__label">{t('publishRecord.publishedLabel')}</span>
                                      <span>{formatUpdatedAt(publishedAt)}</span>
                                    </div>
                                    <div className="tiktok-record-times__row">
                                      <span className="tiktok-record-times__label">{t('publishRecord.listedLabel')}</span>
                                      <span>
                                        {shelfLabel === '自动上架' || shelfLabel === '—'
                                          ? (shelfLabel === '自动上架' ? t('publishRecord.autoList') : shelfLabel)
                                          : formatUpdatedAt(shelfLabel)}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="tiktok-product-cell tiktok-draft-cell--actions"
                                >
                                  <div className="tiktok-draft-actions">
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      disabled={loading || editingId === item.id}
                                      onClick={() => onEdit(item)}
                                    >
                                      {editingId === item.id ? t('publishRecord.checking') : t('publishRecord.edit')}
                                    </button>
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      disabled={loading || copyingId === item.id || editingId === item.id}
                                      onClick={() => onCopyToDraft(item)}
                                    >
                                      {copyingId === item.id ? t('publishRecord.copying') : t('publishRecord.copy')}
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : null}
                          </tr>
                        );
                      });
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <TiktokOpsPager
            page={page}
            pageSize={pageSize}
            total={total}
            loading={loading}
            disabled={!shopId}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            ariaLabel={t('publishRecord.pager')}
          />
        </div>
      </ProductListCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalTitle}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta?.placeholder ? t(batchModalMeta.placeholder) : ''}
        onConfirm={onBatchConfirm}
        onClose={() => setBatchModalField(null)}
      />
    </>
  );
}
